import { createTheme } from '@mui/material/styles';

// JY Law brand identity (Option A — Classical Counsel).
// Source of truth: ../../../../brand.yaml at platform root.
// Hand-curated theme — sibling files RTLProvider.tsx + moduleColors.ts —
// intentionally diverged from the codegen theme.ts pattern (spec-09 unified portal).

const ARABIC_FONT_STACK = '"Noto Naskh Arabic", "IBM Plex Sans Arabic", "Tajawal"';
const LATIN_BODY_STACK = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';
const LATIN_HEADING_STACK = '"Source Serif 4", "Source Serif Pro", Georgia, "Times New Roman", serif';
const MONO_STACK = '"JetBrains Mono", "Menlo", "Consolas", monospace';

// Type augmentation — Client 360 (KYC design 2026-05-03) introduces
// theme.palette.surfaceBorder (DESIGN.md tension #1 resolution) +
// theme.typography.mono (DESIGN.md tension #4 — explicit mono variant
// for ref codes / IDs / regex pattern badges).
declare module '@mui/material/styles' {
  interface Palette { surfaceBorder: string; }
  interface PaletteOptions { surfaceBorder?: string; }
  interface TypographyVariants { mono: React.CSSProperties; }
  interface TypographyVariantsOptions { mono?: React.CSSProperties; }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides { mono: true; }
}

export const jylawTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0B2545', light: '#234E70', dark: '#061629', contrastText: '#FFFFFF' },
    secondary: { main: '#A47148', light: '#C2956C', dark: '#7C5232', contrastText: '#FFFFFF' },
    error: { main: '#B23A48' },
    warning: { main: '#C77A1F' },
    success: { main: '#3E6B47' },
    info: { main: '#234E70' },
    // DESIGN.md tension #3 — paper drifts to warm-near-white per "no pure white" rule
    background: { default: '#FAFAF7', paper: '#FDFCF8' },
    text: { primary: '#0E1116', secondary: '#4A5568', disabled: '#9AA0A6' },
    divider: 'rgba(11, 37, 69, 0.12)',
    // DESIGN.md tension #1 — hairline border on cards, previously hardcoded as #E3E7EC at call sites
    surfaceBorder: '#E3E7EC',
  },
  typography: {
    fontFamily: `${LATIN_BODY_STACK}, ${ARABIC_FONT_STACK}`,
    h1: { fontFamily: `${LATIN_HEADING_STACK}, ${ARABIC_FONT_STACK}`, fontWeight: 600, letterSpacing: '-0.01em' },
    h2: { fontFamily: `${LATIN_HEADING_STACK}, ${ARABIC_FONT_STACK}`, fontWeight: 600, letterSpacing: '-0.005em' },
    h3: { fontFamily: `${LATIN_HEADING_STACK}, ${ARABIC_FONT_STACK}`, fontWeight: 600 },
    h4: { fontFamily: `${LATIN_HEADING_STACK}, ${ARABIC_FONT_STACK}`, fontWeight: 600 },
    h5: { fontFamily: `${LATIN_HEADING_STACK}, ${ARABIC_FONT_STACK}`, fontWeight: 600 },
    h6: { fontFamily: `${LATIN_HEADING_STACK}, ${ARABIC_FONT_STACK}`, fontWeight: 600 },
    // DESIGN.md tension #4 — explicit body/caption/overline overrides per YAML frontmatter
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.43 },
    caption: { fontSize: '0.75rem', lineHeight: 1.4, letterSpacing: '0.025em' },
    overline: { fontSize: '0.65rem', lineHeight: 1.4, letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 500 },
    mono: { fontFamily: MONO_STACK, fontSize: '0.875rem', lineHeight: 1.5 },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: '0 1px 0 rgba(11, 37, 69, 0.08)' },
      },
      defaultProps: { elevation: 0 },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 6 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 6 },
        // DESIGN.md tension #1 — hairline-bordered Papers reference the new token
        outlined: ({ theme }) => ({ borderColor: theme.palette.surfaceBorder }),
      },
    },
    // DESIGN.md tension #7 — kill the Material-stock 1dp Card shadow; hairline-not-shadow silhouette
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: { root: { boxShadow: 'none' } },
    },
  },
});

export const BRAND = {
  displayName: 'JY Law',
  displayNameAr: 'جي واي لو',
  logoSrc: '/logo.svg',
  logoSrcDark: '/logo-dark.svg',
  faviconSrc: '/favicon.svg',
} as const;

export { MODULE_CONFIGS, MODULE_LIST } from './moduleColors';
export type { ModuleId, ModuleConfig } from './moduleColors';
